/**
 * Pin 진입점 헬퍼 (단일 소스: config/pin.php + App\Pin\Support\PinSeo)
 *
 * 랜딩(SPA)에서 Pin(/pin/*, 서버 렌더)으로 나가는 링크는 **서버가 정한 값**을 써야 한다.
 *   ① 켜졌는가 — PIN_ENABLED=false 인 환경에서는 /pin/* 라우트가 아예 등록되지 않아 404 다.
 *      dev 는 오피넷 API 쿼터를 prod 와 공유해 계속 꺼둔 상태라, 플래그 없이 링크를 박으면
 *      개발 환경 홈이 404 로 안내하게 된다. prod 롤백 시에도 링크가 자동으로 사라진다.
 *   ② 경로 — PinSeo::restAreaIndexPath() 가 Pin 내부링크 경로의 SSOT 다.
 *
 * 둘 다 landing.blade.php 가 `window.JadEye.pin` 으로 주입한다(꺼져 있으면 null).
 * 관련 문서: docs/pin/README.md · docs/dev-notes/pin-서비스.md
 */

export interface PinEntry {
    /**
     * 제품 소개 페이지 — 예: '/pin'. **사람이 보는 진입점**(앱바·카드·푸터)은 전부 여기로 간다.
     * JadEye 의 '/jadeye', Gridverse 의 '/gridverse' 와 같은 층이다(랜딩 SPA 라우트).
     */
    landing: string;

    /**
     * 휴게소 콘텐츠 허브 — 예: '/pin/rest-area'. **크롤러가 읽는 링크**(noscript)와
     * 소개 페이지의 CTA 가 여기로 간다. 소개 페이지는 SPA 라 서버 HTML 이 비어 있어
     * 크롤러에게는 의미가 없다 — 크롤러에겐 본문이 있는 SSR URL 을 줘야 한다.
     */
    hub: string;

    /**
     * 노포 맛집 허브 — 예: '/pin/restaurant'. 두 번째 콘텐츠 카테고리(2026-08-17).
     * PIN_OLD_ENABLED 가 꺼진 환경에서는 null — 소개 페이지 메뉴에서 자동으로 빠진다.
     */
    oldHub?: string | null;

    /** 데이터 출처와 한계 문서 — 예: '/pin/legal/data-sources'. 소개 페이지의 "이렇게 만들어요" 블록이 링크한다. */
    dataSources?: string | null;

    /**
     * 콘텐츠 축 전체 — 서버의 `PinSeo::nav()` 를 그대로 옮긴 것이다.
     *
     * 🚨 **소개 페이지가 그리는 목록은 이것 하나뿐이어야 한다.** 축을 컴포넌트마다 손으로
     *    나열하던 시절, 타임터널(2026-08-18)과 등산(2026-09-08)이 발행됐는데도 `/pin` 에는
     *    휴게소·노포 둘만 보였다 — SSR 은 nav() SSOT 라 4축이 정상이었고 SPA 만 뒤처졌다.
     *    이제 축이 늘면 서버가 알아서 채우고 화면이 저절로 따라온다.
     *
     * 각 축의 하위 플래그(PIN_OLD_ENABLED 등)는 서버 nav() 안에서 이미 걸리므로,
     * 여기 담긴 축은 **전부 실제로 200 을 응답하는 URL** 이다.
     */
    axes?: PinAxis[];
}

/** 콘텐츠 축 하나 — label 은 SSR GNB 와 **같은 라벨**이다(같은 것을 다르게 부르지 않는다). */
export interface PinAxis {
    label: string;
    path: string;
}

/**
 * Pin 진입점 반환. **꺼져 있거나 주입되지 않았으면 null** 이다.
 *
 * getCompany() 와 달리 예외를 던지지 않는다 — "주입되지 않음" 이 정상 상태(기능 꺼짐)이기 때문이다.
 *
 * ℹ️ window 타입을 지역 캐스팅으로 좁힌다. company.ts 의 `declare global` 이 JadEye 를
 *    익명 객체 타입으로 선언해 두어, 다른 파일에서 같은 속성을 다시 선언하면 TS 가 충돌로 본다.
 *    LandingApp.svelte 가 window.__SEO__ 를 읽을 때 쓰는 방식과 같다.
 */
export function getPin(): PinEntry | null {
    if (typeof window === 'undefined') {
        return null;
    }

    const pin = (window as unknown as { JadEye?: { pin?: PinEntry | null } }).JadEye?.pin;

    return pin?.landing && pin?.hub ? pin : null;
}
